import { ErrorHandler, Injectable, Provider } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';
import { isLocalhost } from '../../../main';

/**
 * Custom ErrorHandler that sends unhandled exceptions to PostHog.
 * Works alongside your PosthogService (which initializes the base client).
 */
@Injectable({ providedIn: 'root' })
class PostHogErrorHandler implements ErrorHandler {
  public handleError(error: unknown): void {
    // Capture error only if error tracking is enabled
    if (!environment.postHogEnabled) {
      console.warn('[PostHogErrorHandler] Skipped: error tracking disabled');
      console.error(error);
      return;
    }

    const extractedError = this._extractError(error) || 'Unknown error';
    runOutsideAngular(() => {
      try {
        if (posthog?.captureException) {
          posthog.captureException(extractedError);
        } else {
          // Fallback for SDK versions that don't have captureException
          posthog.capture('angular_error', { error: extractedError });
        }
      } catch (err) {
        console.error('[PostHogErrorHandler] Failed to send error:', err);
      }
    });

    // Still log in console for local debugging
    console.error(extractedError);
  }

  private _extractError(errorCandidate: unknown): unknown {
    const error = tryToUnwrapZonejsError(errorCandidate);

    if (error instanceof HttpErrorResponse) {
      return extractHttpModuleError(error);
    }

    if (typeof error === 'string' || isErrorOrErrorLikeObject(error)) {
      return error;
    }

    return null;
  }
}

// Utility functions (same as PostHog doc)
function tryToUnwrapZonejsError(error: unknown): unknown | Error {
  return error && (error as { ngOriginalError: Error }).ngOriginalError
    ? (error as { ngOriginalError: Error }).ngOriginalError
    : error;
}

function extractHttpModuleError(error: HttpErrorResponse): string | Error {
  if (isErrorOrErrorLikeObject(error.error)) {
    return error.error;
  }

  if (
    typeof ErrorEvent !== 'undefined' &&
    error.error instanceof ErrorEvent &&
    error.error.message
  ) {
    return error.error.message;
  }

  if (typeof error.error === 'string') {
    return `Server returned code ${error.status} with body "${error.error}"`;
  }

  return error.message;
}

function isErrorOrErrorLikeObject(value: unknown): value is Error {
  if (value instanceof Error) return true;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return 'name' in value && 'message' in value && 'stack' in value;
}

// Angular Zone safe runner
declare const Zone: any;
const isNgZoneEnabled = typeof Zone !== 'undefined' && Zone.root?.run;

export function runOutsideAngular<T>(callback: () => T): T {
  return isNgZoneEnabled ? Zone.root.run(callback) : callback();
}

/**
 * Provider to register PostHogErrorHandler globally in Angular.
 */
export function providePostHogErrorHandler(): Provider {
  if (environment.postHogEnabled && !isLocalhost) {
    return {
      provide: ErrorHandler,
      useClass: PostHogErrorHandler,
    };
  }

  return {
    provide: ErrorHandler,
    useClass: ErrorHandler, // fallback to Angular default
  };
}
